import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';

import { combineLatest, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { ApplicationService, RequestInfo, SearchParameters, CategoryInfo } from 'services/application/application.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { UserInfo, UserService } from 'services/user/user.service';

@Component({
    selector: 'app-referrals',
    styleUrls: ['referrals.component.scss'],
    templateUrl: 'referrals.component.html',
})
export class ReferralsComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AllCategories: CategoryInfo[];
    AllHeaders: string[] = [
        'DesigneeFlag',
        'RequestIssueNumber',
        'BoardMember',
        'DueDate',
        'Priority',
        'Subject',
        'CategoryLabel',
        'StatusLabel',
        'DateCreated',
        // 'DateClosed',
        'Actions',
    ];
    HasDesigneeEntries: boolean;
    Referrals: RequestInfo[];
    NoData: boolean;
    ViewReady: boolean;
    SearchConducted: boolean;
    SearchParams: SearchParameters;
    SmallDisplay: boolean;
    ReferralsData: MatTableDataSource<RequestInfo>;
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private errorService: ErrorService,
        private router: Router,
        private screenSizeService: ScreenSizeService,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AllCategories = null;
        self.Referrals = null;
        self.NoData = false;
        self.SearchConducted = false;
        self.SearchParams = <SearchParameters>{
            srchReferredTo: '',
        };
        self.User = null;
        self.ViewReady = false;
        self.ReferralsData = new MatTableDataSource<RequestInfo>([]);

        self.SmallDisplay = self.screenSizeService.ScreenSize.IsSmallSize;

        self.screenSizeService.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(size => {
            self.SmallDisplay = size.IsSmallSize;
            self.VisibleHeaders = self.SmallDisplay
                ? ['SmallDisplay']
                : self.HasDesigneeEntries
                ? self.AllHeaders.slice()
                : self.AllHeaders.filter(h => h !== 'DesigneeFlag');
            self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            self.SearchParams.srchReferredTo = self.User.SAPID;

            // Load data for use in the compoment.
            const allCategoriesObs = self.appService.GetAllCategories();
            const allReferralsObs = self.appService.GetAllReferralsForSAPID(
                self.User.AuthToken,
                self.User.CurrentRole,
                self.User.SAPID
            );

            combineLatest(allCategoriesObs, allReferralsObs)
                .pipe(
                    takeUntil(self._subrelease),
                    map(_packet => {
                        return {
                            Categories: _packet[0],
                            Referrals: _packet[1],
                        };
                    })
                )
                .subscribe(
                    packet => {
                        self.AllCategories = packet.Categories || <CategoryInfo[]>[];
                        self.Referrals = packet.Referrals || <RequestInfo[]>[];
                        self.NoData = !self.Referrals.length;
                        self.HasDesigneeEntries = self.Referrals.findIndex(r => r.AsDesignee === true) >= 0;
                        self.ReferralsData.data = self.NoData ? [<RequestInfo>{}] : self.Referrals;

                        self.VisibleHeaders = self.SmallDisplay
                            ? ['SmallDisplay']
                            : self.HasDesigneeEntries
                            ? self.AllHeaders.slice()
                            : self.AllHeaders.filter(h => h !== 'DesigneeFlag');
                        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                        self.SearchConducted = true;
                        self.ViewReady = true;
                    },
                    error => self.errorService.HandleServiceError(error)
                );
        });
    }

    // #region Handle Search

    HandleSearch() {
        const self = this;

        self.appService.PerformSearch(self.User.AuthToken, self.User.CurrentRole, self.SearchParams).subscribe(
            results => {
                self.Referrals = results || <RequestInfo[]>[];
                self.NoData = !self.Referrals.length;
                self.ReferralsData.data = self.NoData ? [<RequestInfo>{}] : self.Referrals;

                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                self.SearchConducted = true;
            },
            error => self.errorService.HandleServiceError(error)
        );
    }

    // #endregion

    // #region Handle View

    HandleView(req: RequestInfo) {
        const self = this;

        if (!req || req.RequestID == null) {
            return;
        }

        self.router.navigate(['/handlerequest', req.RequestID, req.ReferralID, 'referrals']);
    }

    // #endregion
}
